import { defineComponent, reactive, SetupContext, readonly, onMounted, ref } from '@vue/composition-api';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import { MaterialDbDelType } from '@/shared/type';
import HttpStatus from 'http-status-codes';
import vueRouter from '@/router/index';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };   // Windowサイズ
    tab: number;                            // タブ
    material: MaterialDbDelType;            // 教材
}

/**
 * 教材削除画面コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const commonRef = ref<any>(null);                                   // メッセージダイアログ
        const router = vueRouter;                                           // ルータ
        let materialId = '';                                                // 削除対象の教材ID
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                     // ウィンドウサイズ
            tab: 1,
            material: {
                title: '',                                                  // タイトル
                urls: '',                                                   // 教材URL
                materialFile: [],                                           // 教材ファイル
                schoolTypeName: '',                                         // 校種名
                gradeName: '',                                              // 学年名
                curriculumName: '',                                         // 教科・科目名
                name: '',                                                   // 氏名
                publicationRangeName: '',                                   // 公開範囲名称
                useTermsName: '',                                           // 利用条件名称
                summary: '',                                                // 概要
                thumbnailFile: [],                                          // サムネイル
                organizationName: '',                                       // 所属名
                mail: '',                                                   // メールアドレス
                searchKeyword: '',                                          // 検索キーワード
                copyright: '',                                              // 著作
                operationEnvironment: '',                                   // 動作環境
                remarks: ''                                                 // 備考
            }
        });

        /**
         * 初期処理
         */
        onMounted(() => {
            init();
        });
        async function init() {
            // 教材編集・削除初期表示取得APIで初期表示データを取得する
            const response = await ctx.root.$store.dispatch('materialDb/getOperateMaterial',
                {
                    materialId: materialId
                }
            );

            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    /// HTTPステータスコードが404の場合、displayErrorMessageTransitionDialogメソッドで
                    // 教材一覧画面へ戻る
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/material-db/list');
                } else {
                    // HTTPステータスコードが200, 404以外の場合
                    // errorDisplayTransitionControlメソッドでエラー画面へ遷移して処理を終了する
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // 画面へデータを設定する（ファイルサイズは1024で割り有効桁数3桁で表示する）
            response.data.material.materialFiles.forEach(
                (x: { fileSize: number, fileSizeKb: string }) => { x.fileSizeKb = (x.fileSize / 1024).toFixed(3) });
            response.data.material.thumbnailFiles.forEach(
                (x: { fileSize: number, fileSizeKb: string }) => { x.fileSizeKb = (x.fileSize / 1024).toFixed(3) });
            state.material = response.data.material;
        }

        /**
         * 初期表示
         */
        created();
        async function created() {
            // 削除対象教材ID取得
            materialId = ctx.root.$route.params.id;

            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-database-remove');

            // mainTitleを「教材削除」に変更
            ctx.root.$store.commit('setMainTitle', '教材削除');
        }

        /**
         * 削除確認ダイアログ表示
         */
        function displayDialog() {
            const displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['削除']);
            // 確認メッセージダイアログ表示
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * 確認メッセージダイアログの「はい」押下時の処理
         */
        async function processingData() {
            // 教材削除APIを呼び出す
            // パラメータ: 教材ID
            const response = await ctx.root.$store.dispatch('materialDb/deleteMaterial',
                {
                    'materialId': materialId
                }
            );

            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    //  HTTPステータスコードが404の場合
                    //  displayErrorMessageTransitionDialogメソッドで教材一覧に遷移
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/material-db/list');
                } else {
                    //  HTTPステータスコードが404以外の場合
                    //  errorDisplayTransitionControlメソッドでエラー画面を表示する
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }
            // HTTPステータスコードが200の場合
            //  削除完了メッセージダイアログを表示して教材一覧に遷移
            commonRef.value.displayMessageTransitionDialog(response, '/material-db/list');
        }

        /**
         * キャンセルボタン押下時の処理
         */
        function back() {
            // storeのページの最終データフラグをfalseに設定
            ctx.root.$store.commit('materialDb/setIsPageLastData', false);

            router.push('/material-db/list');
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state: readonly(state),
            commonRef,
            displayDialog,
            processingData,
            back,
            onResize
        };
    }
});
